import { legSearchParams } from './explorer_request.js';
import { offersOf } from './explorer_legs.js';
import type { ExplorerLegPhase, ExplorerState } from './explorer_state.js';
import type { FlightExplorerRequest, LegFailure } from './search_source.js';

/*
 * A volta ao quadro anterior quando a re-busca da COLUNA (a troca de cabine) não
 * entrega lista: falhou, ou pousou vazia. Fora do `explorer_state.ts` pelo teto de
 * linhas dele.
 */

/** O que a re-busca deixou de entregar: a causa da falha, ou `empty` — pousou sem
 *  oferta nenhuma, e repetir a mesma busca devolveria vazio de novo. */
export type ResearchOutcome = { failure?: LegFailure } | { empty: true };

/**
 * O quadro que a troca de cabine no one_way/RT refaz (#6438). Lá a cabine dispara
 * `search_started`, que re-forma o pedido e zera tudo — legítimo para a faixa (rota
 * e datas novas são outra viagem), destrutivo para a cabine: a mesma viagem, e a
 * busca em Primeira que volta vazia (ou a do fornecedor que falha) deixava a
 * pessoa sem a lista, sem a ida escolhida do RT e com o "Tentar de novo" preso na
 * cabine que falhou. A cena inteira é guardada — não só a perna 0 como no
 * `fallback` do multi (#3213) — porque na fase 2 do RT o que está em cena é a
 * perna 1 (os pares da ida escolhida).
 *
 * Os `seqs` ficam de fora de propósito: nunca encolhem (o espelho de IO do
 * componente), e a perna 1 restaurada continua com o seq que a pousou.
 */
export type ExplorerScene = Pick<
  ExplorerState,
  | 'request'
  | 'activeLeg'
  | 'legs'
  | 'seeds'
  | 'chosen'
  | 'filter'
  | 'chips'
  | 'serverOrderedLeg'
  | 'picksDiscarded'
  | 'outboundGone'
>;

/** A cena a guardar no disparo — `null` quando não há lista em cena para resgatar. */
export function sceneOf(state: ExplorerState): ExplorerScene | null {
  if (!state.legs.some((leg) => offersOf(leg).length > 0)) return null;
  return {
    request: state.request,
    activeLeg: state.activeLeg,
    legs: state.legs,
    seeds: state.seeds,
    chosen: state.chosen,
    filter: state.filter,
    chips: state.chips,
    serverOrderedLeg: state.serverOrderedLeg,
    picksDiscarded: state.picksDiscarded,
    ...(state.outboundGone ? { outboundGone: state.outboundGone } : {}),
  };
}

/** Devolve a cena guardada, com o aviso recuperável na perna que estava ativa.
 *  `params` são os da TENTATIVA (o pedido corrente, que a re-forma reescreveu). */
export function restoreScene(
  state: ExplorerState,
  scene: ExplorerScene,
  outcome: ResearchOutcome,
): ExplorerState {
  return {
    ...state,
    outboundGone: false,
    ...scene,
    sceneFallback: null,
    legRollback: { leg: scene.activeLeg, params: legSearchParams(state.request, 0), ...outcome },
  };
}

/**
 * O pouso VAZIO de uma re-busca que tinha cena guardada: a troca de cabine do
 * one_way/RT (perna 0) ou a re-busca de perna do multi. `null` = pouso normal.
 */
export function emptyLandingRollback(
  state: ExplorerState,
  leg: number,
  was: ExplorerLegPhase,
  offers: readonly unknown[],
): ExplorerState | null {
  if (offers.length > 0) return null;
  if (leg === 0 && state.sceneFallback) return restoreScene(state, state.sceneFallback, EMPTY);
  if (was.phase === 'loading' && was.fallback) return restoreLeg(state, leg, was.fallback, EMPTY);
  return null;
}

const EMPTY = { empty: true } as const;

type LegFallback = NonNullable<Extract<ExplorerLegPhase, { phase: 'loading' }>['fallback']>;

/**
 * A re-busca de UMA perna (multi, #3213) volta ao quadro guardado: a lista, a data
 * e a cabine que ela reescreveu no pedido, e o corte da perna ativa. O aviso leva
 * os params da tentativa — é o que o "Tentar de novo" re-dispara.
 */
export function restoreLeg(
  state: ExplorerState,
  leg: number,
  fallback: LegFallback,
  outcome: ResearchOutcome,
): ExplorerState {
  const { offers, departDate, cabin, filter } = fallback;
  const legs = [...state.legs];
  legs[leg] = { phase: 'ready', offers };
  const request: FlightExplorerRequest = {
    ...state.request,
    legs: state.request.legs.map((l, i) => (i === leg ? { ...l, departDate } : l)),
    ...(cabin ? { cabin } : {}),
  };
  if (!cabin) delete request.cabin;
  return {
    ...state,
    legs,
    request,
    legRollback: { leg, params: legSearchParams(state.request, leg), ...outcome },
    ...(filter && leg === state.activeLeg ? { filter } : {}),
  };
}
